import {
  Activity,
  Calculator,
  Flag,
  FolderKanban,
  DoorOpen,
  Globe,
  Inbox,
  FileCog,
  Gavel,
  Layers,
  LayoutDashboard,
  ListChecks,
  Menu,
  Power,
  ShieldCheck,
  Smartphone,
  Users,
  Wallet,
} from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, useLocation } from 'react-router';
import type { Action } from '@artosera/shared/permissions';
import { Button, Logo, ThemeToggle } from '@artosera/ui';
import { deconnexion } from '../../lib/api.js';
import { CLE_SESSION, useSession } from '../../auth/useSession.js';
import { usePeut } from '../../auth/usePeut.js';
import pageStyles from '../../styles/page.module.css';
import styles from './ConsoleShell.module.css';

/**
 * Chaque entrée porte l'action de la matrice qui l'ouvre : le shell ne compare
 * jamais un rôle. L'associé n'a que `console:read-overview`, donc que la vue
 * d'aigle (ARTO-58) ; l'API refuse le reste de son côté.
 */
const ENTREES = [
  {
    chemin: '/vue-d-aigle',
    cle: 'nav.vueDAigle',
    icone: LayoutDashboard,
    action: 'console:read-overview',
  },
  // La comptabilité de la vue d'aigle : la direction et les associés (leur part seule).
  {
    chemin: '/comptabilite',
    cle: 'nav.comptabilite',
    icone: Calculator,
    action: 'console:read-accounting',
  },
  { chemin: '/galeries', cle: 'nav.galeries', icone: FolderKanban, action: 'console:read' },
  // Les demandes d'ouverture venues d'artosera.com (ARTO-188) : avant les dossiers, qu'elles préparent.
  { chemin: '/demandes', cle: 'nav.demandes', icone: Inbox, action: 'console:read-onboarding' },
  // Les dossiers de mise en service (ARTO-128).
  {
    chemin: '/ouvertures',
    cle: 'nav.ouvertures',
    icone: DoorOpen,
    action: 'console:read-onboarding',
  },
  // Les demandes de création de site envoyées par les galeries.
  { chemin: '/demandes-site', cle: 'nav.demandesSite', icone: Globe, action: 'console:read' },
  // Les plantages et blocages de l'app iOS.
  {
    chemin: '/diagnostics-ios',
    cle: 'nav.diagnosticsIos',
    icone: Smartphone,
    action: 'console:read',
  },
  // La bibliothèque des profils de correspondance de la reprise (ARTO-130).
  {
    chemin: '/reprise/profils',
    cle: 'nav.profils',
    icone: FileCog,
    action: 'console:manage-import-profiles',
  },
  { chemin: '/travaux', cle: 'nav.travaux', icone: ListChecks, action: 'console:read' },
  { chemin: '/journal', cle: 'nav.journal', icone: Gavel, action: 'console:read' },
  { chemin: '/sante', cle: 'nav.sante', icone: Activity, action: 'console:read' },
  // Saisies financières : l'exploitation et la direction.
  {
    chemin: '/finances',
    cle: 'nav.finances',
    icone: Wallet,
    action: 'console:manage-subscriptions',
  },
  // Réservés à la direction de Venio : jalons, comptes associés et partage.
  { chemin: '/jalons', cle: 'nav.jalons', icone: Flag, action: 'console:manage-milestones' },
  { chemin: '/associes', cle: 'nav.associes', icone: Users, action: 'console:manage-associates' },
  // Versions minimale et conseillée des apps : lisibles par l'exploitation, réglées par elle et la direction.
  { chemin: '/apps-mobiles', cle: 'nav.appsMobiles', icone: Smartphone, action: 'console:read' },
  // Paliers de développement : lisibles par l'exploitation, modifiables par la direction.
  {
    chemin: '/addons/ventes',
    cle: 'ventes.titre',
    icone: Layers,
    action: 'console:read-addon-sales',
  },
  { chemin: '/addons', cle: 'addons.titre', icone: Layers, action: 'console:read' },
  { chemin: '/paliers', cle: 'nav.paliers', icone: Layers, action: 'console:read' },
  // Réservé à la direction de Venio (D21) : couper un module pour tout le parc.
  {
    chemin: '/interrupteurs',
    cle: 'nav.interrupteurs',
    icone: Power,
    action: 'console:kill-switch',
  },
] as const;

/** Les actions que seule la direction de Venio peut faire (matrice des droits). */
const ACTIONS_DIRECTION: readonly Action[] = [
  'console:manage-milestones',
  'console:manage-associates',
  'console:kill-switch',
];

/** La clé i18n du nom d'un écran : le titre de l'onglet du navigateur le reprend. */
function cleDeLEcran(pathname: string): string {
  if (pathname === '/securite') return 'nav.securite';
  const entree = ENTREES.find(
    ({ chemin }) => pathname === chemin || pathname.startsWith(`${chemin}/`),
  );
  return entree?.cle ?? 'marque.console';
}

const LIBELLES_ROLE: Record<string, string> = {
  venio_associe: 'roles.associe',
  venio_support: 'roles.support',
  venio_exploitation: 'roles.exploitation',
  venio_direction: 'roles.direction',
};

export function ConsoleShell({ children }: { children: ReactNode }): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const { moi, confine } = useSession();
  const peut = usePeut();
  const { pathname, state } = useLocation();
  const accesRefuse = (state as { accesRefuse?: Action } | null)?.accesRefuse;
  const cleEcran = cleDeLEcran(pathname);
  // Un titre par écran : « Galeries — Console Artosera », pas le même partout.
  useEffect(() => {
    document.title = t('session.titreDocument', { page: t(cleEcran) });
  }, [t, cleEcran]);
  // En grâce : le second facteur est exigé, la console reste ouverte, un
  // rappel mène à la sécurité du compte (hors de cet écran lui-même).
  const rappelMfa = moi?.enrolementMfaRequis === true && !confine && pathname !== '/securite';
  const queryClient = useQueryClient();
  // Sidebar repliable (ARTO-53, § 7) : sous 768 px, un tiroir plutôt qu'une
  // barre fixe qui mangerait la moitié de l'écran (audit du 23 septembre
  // 2026, § 1 — recette 390/768/1440 px).
  const [menuOuvert, setMenuOuvert] = useState(false);

  useEffect(() => {
    if (!menuOuvert) return;
    const surEchap = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOuvert(false);
    };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [menuOuvert]);

  const seDeconnecter = useMutation({
    mutationFn: deconnexion,
    onSuccess: () => {
      queryClient.setQueryData(CLE_SESSION, undefined);
      window.location.assign('/connexion');
    },
  });

  return (
    <div className={styles.coquille}>
      {menuOuvert ? (
        <button
          type="button"
          className={styles.scrim}
          aria-label={t('nav.fermerMenu')}
          onClick={() => setMenuOuvert(false)}
        />
      ) : null}
      <aside className={styles.barreLaterale} data-ouverte={menuOuvert ? 'true' : undefined}>
        <div className={styles.marque}>
          <Logo title="Artosera" />
        </div>
        <p className={styles.repereConsole}>{t('marque.console')}</p>
        <nav className={styles.navigation} aria-label={t('nav.aria')}>
          {/* Confinée à l'enrôlement 2FA, la session n'ouvre que la sécurité
              du compte : aucune autre entrée n'est offerte. */}
          {ENTREES.filter((entree) => !confine && peut(entree.action)).map(
            ({ chemin, cle, icone: Icone }) => (
              <NavLink
                key={chemin}
                to={chemin}
                onClick={() => setMenuOuvert(false)}
                className={({ isActive }) =>
                  [styles.lienNav, isActive ? styles.lienNavActif : ''].filter(Boolean).join(' ')
                }
              >
                <Icone size={17} strokeWidth={1.5} aria-hidden="true" />
                <span>{t(cle)}</span>
              </NavLink>
            ),
          )}
        </nav>
        <div className={styles.compte}>
          {moi ? (
            <>
              <p className={styles.compteNom}>{moi.compte.nom}</p>
              <p className={styles.compteRole}>
                {moi.compte.rolePlateforme
                  ? t(LIBELLES_ROLE[moi.compte.rolePlateforme] ?? 'roles.inconnu')
                  : t('roles.inconnu')}
              </p>
            </>
          ) : null}
          {/* La sécurité du compte : ouverte à tout rôle de console. */}
          <NavLink
            to="/securite"
            onClick={() => setMenuOuvert(false)}
            className={({ isActive }) =>
              [styles.lienNav, styles.lienCompte, isActive ? styles.lienNavActif : '']
                .filter(Boolean)
                .join(' ')
            }
          >
            <ShieldCheck size={17} strokeWidth={1.5} aria-hidden="true" />
            <span>{t('nav.securite')}</span>
          </NavLink>
        </div>
      </aside>
      <div className={styles.principal}>
        <header className={styles.barreHaute}>
          <button
            type="button"
            className={styles.menuMobile}
            onClick={() => setMenuOuvert(true)}
            aria-label={t('nav.ouvrirMenu')}
          >
            <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
          </button>
          <span className={styles.titrePage}>{t('marque.titre')}</span>
          <div className={styles.outils}>
            <ThemeToggle lang={i18n.language === 'en' ? 'en' : 'fr'} />
            <Button variant="ghost" size="sm" onClick={() => seDeconnecter.mutate()}>
              {t('session.seDeconnecter')}
            </Button>
          </div>
        </header>
        <main className={styles.contenu}>
          {accesRefuse && pathname === '/vue-d-aigle' ? (
            <p className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`} role="status">
              {ACTIONS_DIRECTION.includes(accesRefuse)
                ? t('session.reserveDirection')
                : t('session.pasOuverte')}
            </p>
          ) : null}
          {rappelMfa ? (
            <p className={pageStyles.banniere} role="status">
              <span>{t('securite.grace')}</span>
              <Link to="/securite">{t('securite.demarrer')}</Link>
            </p>
          ) : null}
          {children}
        </main>
        <footer className={styles.pied}>
          <span>{t('marque.piedDePage')}</span>
          <span className={styles.version}>
            v{__APP_VERSION__} · {__GIT_SHA__}
          </span>
        </footer>
      </div>
    </div>
  );
}
